import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { Hash, Shield, UserMinus, Mail, Settings, Smile, Webhook, UserCheck } from '@tamagui/lucide-icons'
import { useState, useMemo } from 'react'
import { useAppStore } from '@/store'
import { auditLog, users } from '@/mocks'
import type { AuditEvent } from '@/types'

const Container = styled(View, { flex: 1, flexDirection: 'column' })

const Header = styled(View, {
  paddingHorizontal: 32, paddingVertical: 20,
  borderBottomWidth: 1, borderBottomColor: '$borderDefault',
  flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
})

const Title = styled(Text, {
  fontFamily: '$fontHeading', fontWeight: '600', fontSize: 20, color: '$surfaceForeground',
})

const Body = styled(View, { padding: 32, gap: 16, maxWidth: 700 })

const FilterRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
})

const EventItem = styled(View, {
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 12,
  paddingVertical: 12,
  paddingHorizontal: 12,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const IconBox = styled(View, {
  width: 32,
  height: 32,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$sidebar',
  flexShrink: 0,
})

const eventTypes = [
  'all',
  'channel_create',
  'channel_delete',
  'role_create',
  'role_update',
  'member_kick',
  'member_ban',
  'member_unban',
  'invite_create',
  'webhook_create',
  'webhook_delete',
  'server_update',
  'emoji_upload',
] as const

const eventTypeLabels: Record<string, string> = {
  all: 'All Events',
  channel_create: 'Channel Create',
  channel_delete: 'Channel Delete',
  role_create: 'Role Create',
  role_update: 'Role Update',
  member_kick: 'Member Kick',
  member_ban: 'Member Ban',
  member_unban: 'Member Unban',
  invite_create: 'Invite Create',
  webhook_create: 'Webhook Create',
  webhook_delete: 'Webhook Delete',
  server_update: 'Server Update',
  emoji_upload: 'Emoji Upload',
}

function getEventIcon(type: string) {
  switch (type) {
    case 'channel_create':
    case 'channel_delete':
      return Hash
    case 'role_create':
    case 'role_update':
      return Shield
    case 'member_kick':
    case 'member_ban':
      return UserMinus
    case 'member_unban':
      return UserCheck
    case 'invite_create':
      return Mail
    case 'webhook_create':
    case 'webhook_delete':
      return Webhook
    case 'server_update':
      return Settings
    case 'emoji_upload':
      return Smile
    default:
      return Settings
  }
}

function getEventIconColor(type: string): string {
  switch (type) {
    case 'channel_create': return '#3ECFCF'
    case 'channel_delete': return '#CF3E4F'
    case 'role_create':
    case 'role_update': return '#A855F7'
    case 'member_kick':
    case 'member_ban': return '#CF3E4F'
    case 'member_unban': return '#3ECF7B'
    case 'invite_create': return '#3B82F6'
    case 'webhook_create':
    case 'webhook_delete': return '#E8A840'
    case 'server_update': return '#8B95A8'
    case 'emoji_upload': return '#E8A840'
    default: return '#8B95A8'
  }
}

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(timestamp).toLocaleDateString()
}

export function AuditLogSettings() {
  const { activeServerId } = useAppStore()
  const theme = useTheme()
  const [filterType, setFilterType] = useState<string>('all')

  const events = activeServerId ? auditLog[activeServerId] || [] : []

  const filteredEvents = useMemo(() => {
    if (filterType === 'all') return events
    return events.filter(e => e.type === filterType)
  }, [events, filterType])

  return (
    <Container>
      <Header>
        <Title>Audit Log</Title>
      </Header>
      <ScrollView flex={1}>
        <Body>
          <FilterRow>
            <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">Filter by:</Text>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                background: theme.sidebar?.val || '#151921',
                border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
                borderRadius: 6,
                padding: '6px 10px',
                color: theme.surfaceForeground?.val || '#E2E8F0',
                fontFamily: 'inherit',
                fontSize: 13,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              {eventTypes.map((type) => (
                <option key={type} value={type}>{eventTypeLabels[type]}</option>
              ))}
            </select>
          </FilterRow>

          <View borderRadius="$borderRadiusLg" borderWidth={1} borderColor="$borderDefault" overflow="hidden">
            {filteredEvents.map((event) => {
              const actor = users[event.actorId]
              const Icon = getEventIcon(event.type)
              const iconColor = getEventIconColor(event.type)

              return (
                <EventItem key={event.id}>
                  <IconBox>
                    <Icon size={16} color={iconColor} />
                  </IconBox>
                  <View flex={1} gap={2}>
                    <Text fontFamily="$fontBody" fontSize={13} color="$surfaceForeground">
                      {event.details}
                    </Text>
                    <View flexDirection="row" alignItems="center" gap={6}>
                      <Text fontFamily="$fontBody" fontSize={12} fontWeight="600" color={actor?.roleColor || '$sidebarMuted'}>
                        {actor?.displayName || 'Unknown'}
                      </Text>
                      <Text fontFamily="$fontMono" fontSize={11} color="$color8">
                        {formatRelativeTime(event.timestamp)}
                      </Text>
                    </View>
                  </View>
                </EventItem>
              )
            })}

            {filteredEvents.length === 0 && (
              <View padding={32} alignItems="center">
                <Text fontFamily="$fontBody" fontSize={14} color="$sidebarMuted">
                  No audit events found{filterType !== 'all' ? ` for type "${eventTypeLabels[filterType]}"` : ''}.
                </Text>
              </View>
            )}
          </View>
        </Body>
      </ScrollView>
    </Container>
  )
}
